import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFormDropdownChevron,
  AdminCompactFormPage,
  AdminSupportingText,
  AdminHeaderAction,
  AdminFormSecondaryAction,
  AdminReadOnlyField,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
} from '../shared/adminUx';
import { isTeamOwnerActor, isTeamCoOwnerActor } from '../../../data/teamConfig';
import {
  AUTOMATIC_POST_RESTORATION,
  CANCELLATION_TIMING_OPTIONS,
  CONDITIONS_STATEMENT,
  DOWNGRADE_TIMING_OPTIONS,
  DRAFT_POST_BEHAVIOUR,
  GRACE_ELIGIBILITY,
  GST_DISPLAY,
  MINIMUM_PRICE_NOTICE_LABEL,
  PAYMENT_METHOD_DURING_TRIAL,
  REACTIVATION_PAYMENT,
  SOURCE_PLAN_PLACEHOLDER,
  UPGRADE_CHARGE_OPTIONS,
  UPGRADE_TIMING_OPTIONS,
  VISIBILITY_OPTIONS,
  YES_NO_OPTIONS,
  applySourcePlanChange,
  buildSamplePricingPlanConfigurations,
  emptyConfigurationForm,
  findConfigurationById,
  formatCurrentCostDisplay,
  formatEffectiveFromOrDash,
  formatPendingCostDisplay,
  formatPlanCell,
  formatPublicFeaturePreview,
  generatePublicFeatureContent,
  getAvailableSourcePlans,
  getPlanKeyFromManagementPlan,
  getQuoteAllowanceQualificationNote,
  toConfigurationForm,
} from '../../../data/plansConfig';
import {
  SAVE_FAILURE_MESSAGE,
  SAVE_SUCCESS_MESSAGE,
  buildPricingPlanConfigActor,
  canSaveConfiguration,
  collectFieldErrors,
  getPublishedConfiguration,
  mergeConfigurationsAfterSave,
} from '../../../data/pricingPlanWorkflow';
import {
  buildSamplePricingPlanManagementRecords,
  formatAuditDisplay,
  formatGstDisplay,
} from '../../../data/pricingPlanManagementData';

const canAccessPricingPlanConfiguration = (auth) =>
  isTeamOwnerActor(auth) || isTeamCoOwnerActor(auth);

const RequiredMark = () => <span className="text-alertred">*</span>;

function StackedField({ label, required, error, children }) {
  return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>
        {label}
        {required ? (
          <>
            {' '}
            <RequiredMark />
          </>
        ) : null}
      </AdminDetailFieldLabel>
      {children}
      {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
    </div>
  );
}

function NumberAllowanceField({ label, field, value, error, required, placeholder, onChange, min = 0 }) {
  return (
    <StackedField label={label} required={required} error={error}>
      <input
        type="number"
        min={min}
        step="1"
        className={adminFormDetailInputClasses(false, Boolean(error))}
        value={value}
        onChange={(e) => onChange(field, e.target.value)}
        placeholder={placeholder}
      />
    </StackedField>
  );
}

const PlanDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { configId: configIdParam } = useParams();
  const { auth } = useAuth();
  const canAccess = canAccessPricingPlanConfiguration(auth);
  const actor = useMemo(() => buildPricingPlanConfigActor(auth), [auth]);

  const isAdd = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');
  const isViewRoute = !isAdd && !isEdit;

  const [managementPlans] = useState(
    () => location.state?.pricingPlans ?? buildSamplePricingPlanManagementRecords(),
  );
  const [allConfigs, setAllConfigs] = useState(
    () => location.state?.configurations ?? buildSamplePricingPlanConfigurations(managementPlans),
  );

  const resolvedConfig = useMemo(() => {
    if (isAdd || isViewRoute) return null;
    const fromState = location.state?.configuration;
    if (fromState && fromState.id === configIdParam) return fromState;
    return findConfigurationById(allConfigs, configIdParam);
  }, [isAdd, isViewRoute, configIdParam, location.state?.configuration, allConfigs]);

  const publishedBaseline = useMemo(
    () => (isAdd || isViewRoute ? null : getPublishedConfiguration(resolvedConfig)),
    [isAdd, isViewRoute, resolvedConfig],
  );

  const [formData, setFormData] = useState(() =>
    isAdd || isViewRoute ? emptyConfigurationForm() : toConfigurationForm(resolvedConfig),
  );
  const [errors, setErrors] = useState({});
  const [saveMessage, setSaveMessage] = useState('');

  const managementPlan = useMemo(
    () => managementPlans.find((plan) => plan.id === formData.planId) ?? null,
    [managementPlans, formData.planId],
  );

  const availableSourcePlans = useMemo(
    () => getAvailableSourcePlans(allConfigs, managementPlans, isAdd ? undefined : formData.id),
    [allConfigs, managementPlans, isAdd, formData.id],
  );

  const sourcePlanOptions = useMemo(() => {
    if (!isAdd) return availableSourcePlans;
    return availableSourcePlans.length > 0 ? availableSourcePlans : managementPlans;
  }, [isAdd, availableSourcePlans, managementPlans]);

  const planKey =
    formData.planKey || getPlanKeyFromManagementPlan(managementPlan) || resolvedConfig?.planKey || '';

  const publicFeatures = generatePublicFeatureContent({
    ...formData,
    planKey,
  });
  const featurePreviewText = formatPublicFeaturePreview({ ...formData, planKey });
  const quoteNote = getQuoteAllowanceQualificationNote({ ...formData, planKey });

  const showSaveButton =
    isAdd || canSaveConfiguration(formData, publishedBaseline, managementPlan);

  useEffect(() => {
    if (Array.isArray(location.state?.configurations)) {
      setAllConfigs(location.state.configurations);
    }
  }, [location.state?.configurations]);

  useEffect(() => {
    if (isAdd || isViewRoute) return;
    if (resolvedConfig) {
      setFormData(toConfigurationForm(resolvedConfig));
    }
  }, [isAdd, isViewRoute, resolvedConfig]);

  const setField = (field, value) => {
    setErrors((prev) => ({ ...prev, [field]: undefined, form: undefined }));
    setSaveMessage('');
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSourcePlanChange = (planId) => {
    const plan = managementPlans.find((p) => p.id === planId) || null;
    setErrors({});
    setSaveMessage('');
    setFormData((prev) => applySourcePlanChange(prev, plan));
  };

  useEffect(() => {
    if (!isAdd || formData.planId || sourcePlanOptions.length === 0) return;
    const plan = sourcePlanOptions[0];
    if (plan?.id) handleSourcePlanChange(plan.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once when Add opens / options load
  }, [isAdd, sourcePlanOptions, formData.planId]);

  const navigateToList = (nextConfigs, successMessage) => {
    navigate('/admin/configuration/plans', {
      state: {
        configurations: nextConfigs,
        pricingPlans: managementPlans,
        ...(successMessage ? { saveSuccess: successMessage } : {}),
      },
    });
  };

  const handleCancel = () => {
    navigateToList(allConfigs);
  };

  const handleSave = () => {
    const fieldErrors = collectFieldErrors(formData, allConfigs, managementPlan, { isNew: isAdd });
    if (Object.keys(fieldErrors).length) {
      setErrors({ ...fieldErrors, form: SAVE_FAILURE_MESSAGE });
      setSaveMessage(SAVE_FAILURE_MESSAGE);
      return;
    }

    const result = mergeConfigurationsAfterSave(
      allConfigs,
      formData,
      publishedBaseline,
      managementPlan,
      actor,
      { isNew: isAdd },
    );

    if (!result.success) {
      setErrors({ ...(result.errors || {}), form: result.message || SAVE_FAILURE_MESSAGE });
      setSaveMessage(result.message || SAVE_FAILURE_MESSAGE);
      return;
    }

    setErrors({});
    setSaveMessage('');
    setAllConfigs(result.configurations);
    navigateToList(result.configurations, result.noChange ? undefined : SAVE_SUCCESS_MESSAGE);
  };

  // View is a Screen 1 popup — deep links without /add or /edit return to the list.
  if (isViewRoute) {
    return <Navigate to="/admin/configuration/plans" replace />;
  }

  if (!canAccess) {
    return (
      <AdminLayout>
        <AdminPageTitle>Pricing Plan</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray mt-4">
          You do not have permission to manage Pricing Plan configuration. Access is limited to
          Owner and Co-owner.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  if (!isAdd && !resolvedConfig) {
    return (
      <AdminLayout>
        <AdminPageTitle>Pricing Plan not found</AdminPageTitle>
        <div className="mt-4">
          <AdminFormSecondaryAction type="button" onClick={handleCancel}>
            Back to list
          </AdminFormSecondaryAction>
        </div>
      </AdminLayout>
    );
  }

  const pageHeading = isAdd ? 'Add Pricing Plan' : 'Edit Pricing Plan';
  const showTrialLength = planKey === 'basic' && formData.trialIncluded === 'Yes';

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={pageHeading}
        onSubmit={handleSave}
        showSave={false}
        beforeForm={
          saveMessage || errors.form ? (
            <AdminFormFieldError className="mb-4">{saveMessage || errors.form}</AdminFormFieldError>
          ) : null
        }
      >
        {!isAdd ? <AdminReadOnlyField label="Config ID" value={formData.id} mono /> : null}

        <StackedField label="Source Plan" required={isAdd} error={errors.planId}>
          {!isAdd ? (
            <input
              type="text"
              value={formatPlanCell(
                formData.planId,
                formData.planName || managementPlan?.planName,
              )}
              readOnly
              className={adminFormDetailInputClasses(true)}
            />
          ) : (
            <div className="relative w-full min-w-0">
              <select
                className={adminFormDetailSelectClasses(false, Boolean(errors.planId))}
                value={formData.planId || ''}
                onChange={(e) => handleSourcePlanChange(e.target.value)}
              >
                <option value="">{SOURCE_PLAN_PLACEHOLDER}</option>
                {sourcePlanOptions.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {formatPlanCell(plan.id, plan.planName)}
                  </option>
                ))}
              </select>
              <AdminFormDropdownChevron />
            </div>
          )}
        </StackedField>

        <AdminReadOnlyField label="Conditions" value={CONDITIONS_STATEMENT} />
        {errors.conditions ? <AdminFormFieldError>{errors.conditions}</AdminFormFieldError> : null}

        {/* Source details (read-only from management plan) */}
        {managementPlan ? (
          <>
            <AdminReadOnlyField label="Plan ID" value={managementPlan.id} mono />
            <AdminReadOnlyField label="Plan Name" value={managementPlan.planName} />
            <AdminReadOnlyField
              label="Current Monthly Cost"
              value={formatCurrentCostDisplay(managementPlan)}
            />
            <AdminReadOnlyField
              label="Pending Monthly Cost"
              value={formatPendingCostDisplay(managementPlan)}
            />
            <AdminReadOnlyField
              label="Effective From"
              value={formatEffectiveFromOrDash(managementPlan)}
            />
            <AdminReadOnlyField
              label="GST"
              value={formatGstDisplay(managementPlan.gstIncluded) || GST_DISPLAY}
            />
            <AdminReadOnlyField label="Billing Cycle" value={managementPlan.billingCycle} />
            <div className="space-y-1 w-full min-w-0">
              <AdminDetailFieldLabel>Public Description</AdminDetailFieldLabel>
              <textarea
                value={managementPlan.publicDescription || '—'}
                readOnly
                rows={3}
                className={adminFormDetailTextareaClasses(true)}
              />
            </div>
          </>
        ) : null}

        {/* 2 — Trial */}
        {planKey ? (
          <>
            <AdminPanelTitle className="text-offblack !mb-0 mt-2">Trial</AdminPanelTitle>
            {planKey === 'basic' ? (
              <>
                <StackedField label="Trial Included — Basic" required error={errors.trialIncluded}>
                  <div className="relative w-full min-w-0">
                    <select
                      className={adminFormDetailSelectClasses(false, Boolean(errors.trialIncluded))}
                      value={formData.trialIncluded || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setErrors((prev) => ({
                          ...prev,
                          trialIncluded: undefined,
                          trialLengthDays: undefined,
                          form: undefined,
                        }));
                        setSaveMessage('');
                        setFormData((prev) => ({
                          ...prev,
                          trialIncluded: value,
                          trialLengthDays: value === 'Yes' ? prev.trialLengthDays || '28' : '',
                          paymentMethodDuringTrial:
                            value === 'Yes' ? PAYMENT_METHOD_DURING_TRIAL : '',
                        }));
                      }}
                    >
                      <option value="">Select</option>
                      {YES_NO_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    <AdminFormDropdownChevron />
                  </div>
                </StackedField>
                {showTrialLength ? (
                  <StackedField
                    label="Basic Trial Length"
                    required
                    error={errors.trialLengthDays}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        step="1"
                        className={adminFormDetailInputClasses(
                          false,
                          Boolean(errors.trialLengthDays),
                        )}
                        value={formData.trialLengthDays}
                        onChange={(e) => setField('trialLengthDays', e.target.value)}
                        placeholder="Days"
                      />
                      <AdminDetailFieldValue className="shrink-0">Days</AdminDetailFieldValue>
                    </div>
                  </StackedField>
                ) : null}
                {formData.trialIncluded === 'Yes' ? (
                  <AdminReadOnlyField
                    label="Payment Method During Trial"
                    value={PAYMENT_METHOD_DURING_TRIAL}
                  />
                ) : null}
              </>
            ) : (
              <AdminReadOnlyField label="Trial Included — Pro" value="No" />
            )}

            {/* 3 — Allowances */}
            <AdminPanelTitle className="text-offblack !mb-0 mt-2">Allowances</AdminPanelTitle>
            <NumberAllowanceField
              label="Active Post Allowance"
              field="postsAllowed"
              value={formData.postsAllowed}
              error={errors.postsAllowed}
              required
              placeholder="Enter Active Post allowance"
              onChange={setField}
            />
            <NumberAllowanceField
              label="Active Featured Post Allowance"
              field="featuredPostsAllowed"
              value={formData.featuredPostsAllowed}
              error={errors.featuredPostsAllowed}
              required
              placeholder="Enter Active Featured Post allowance"
              onChange={setField}
            />
            <NumberAllowanceField
              label="Quotes Allowed"
              field="quotesAllowed"
              value={formData.quotesAllowed}
              error={errors.quotesAllowed}
              required
              placeholder="Enter Quotes allowance"
              onChange={setField}
            />
            <AdminReadOnlyField label="Draft Post Behaviour" value={DRAFT_POST_BEHAVIOUR} />
            <AdminSupportingText className="text-mediumgray mb-0">{quoteNote}</AdminSupportingText>

            {/* 4 — Profile Access */}
            <AdminPanelTitle className="text-offblack !mb-0 mt-2">Profile Access</AdminPanelTitle>
            <StackedField
              label="Contact Details Visibility"
              required
              error={errors.contactDetailsVisibility}
            >
              <div className="relative w-full min-w-0">
                <select
                  className={adminFormDetailSelectClasses(
                    false,
                    Boolean(errors.contactDetailsVisibility),
                  )}
                  value={formData.contactDetailsVisibility || ''}
                  onChange={(e) => setField('contactDetailsVisibility', e.target.value)}
                >
                  <option value="">Select Contact Details Visibility</option>
                  {VISIBILITY_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                <AdminFormDropdownChevron />
              </div>
            </StackedField>
            <StackedField
              label="Allowed External Links Visibility"
              required
              error={errors.allowedExternalLinksVisibility}
            >
              <div className="relative w-full min-w-0">
                <select
                  className={adminFormDetailSelectClasses(
                    false,
                    Boolean(errors.allowedExternalLinksVisibility),
                  )}
                  value={formData.allowedExternalLinksVisibility || ''}
                  onChange={(e) => setField('allowedExternalLinksVisibility', e.target.value)}
                >
                  <option value="">Select Allowed External Links Visibility</option>
                  {VISIBILITY_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                <AdminFormDropdownChevron />
              </div>
            </StackedField>

            {/* 5 — Grace */}
            <AdminPanelTitle className="text-offblack !mb-0 mt-2">
              Failed-Payment Grace Period
            </AdminPanelTitle>
            <StackedField label="Grace Period" required error={errors.gracePeriodDays}>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  step="1"
                  className={adminFormDetailInputClasses(false, Boolean(errors.gracePeriodDays))}
                  value={formData.gracePeriodDays}
                  onChange={(e) => setField('gracePeriodDays', e.target.value)}
                  placeholder="Days"
                />
                <AdminDetailFieldValue className="shrink-0">Days</AdminDetailFieldValue>
              </div>
            </StackedField>
            <AdminReadOnlyField label="Grace Eligibility" value={GRACE_ELIGIBILITY} />

            {/* 6 — Reactivation */}
            <AdminPanelTitle className="text-offblack !mb-0 mt-2">Reactivation</AdminPanelTitle>
            <AdminReadOnlyField label="Reactivation Payment" value={REACTIVATION_PAYMENT} />
            <AdminReadOnlyField
              label="Automatic Post Restoration"
              value={AUTOMATIC_POST_RESTORATION}
            />

            {/* 7 — Upgrade or Downgrade */}
            <AdminPanelTitle className="text-offblack !mb-0 mt-2">
              {planKey === 'basic' ? 'Basic-to-Pro Upgrade' : 'Pro-to-Basic Downgrade'}
            </AdminPanelTitle>
            {planKey === 'basic' ? (
              <>
                <AdminReadOnlyField
                  label="Upgrade Timing"
                  value={formData.upgradeTiming || UPGRADE_TIMING_OPTIONS[0]}
                />
                <AdminReadOnlyField
                  label="Paid Basic Upgrade Charge"
                  value={formData.upgradeChargeMethod || UPGRADE_CHARGE_OPTIONS[0]}
                />
              </>
            ) : (
              <AdminReadOnlyField
                label="Downgrade Timing"
                value={formData.downgradeTiming || DOWNGRADE_TIMING_OPTIONS[0]}
              />
            )}

            {/* 8 — Cancellation */}
            <AdminPanelTitle className="text-offblack !mb-0 mt-2">
              Subscription Cancellation
            </AdminPanelTitle>
            <AdminReadOnlyField
              label="Cancellation Timing"
              value={formData.cancellationTiming || CANCELLATION_TIMING_OPTIONS[0]}
            />

            {/* 9 — Public Feature Preview */}
            <AdminPanelTitle className="text-offblack !mb-0 mt-2">
              Public Feature Preview
            </AdminPanelTitle>
            <div className="space-y-1 w-full min-w-0">
              <AdminDetailFieldLabel>Generated Public Feature Content</AdminDetailFieldLabel>
              <textarea
                value={featurePreviewText || publicFeatures.join('\n')}
                readOnly
                rows={Math.max(4, publicFeatures.length + 1)}
                className={adminFormDetailTextareaClasses(true)}
              />
            </div>

            {/* 10 — Price Change Notice */}
            <AdminPanelTitle className="text-offblack !mb-0 mt-2">
              Price Change Notice
            </AdminPanelTitle>
            <AdminReadOnlyField
              label="Minimum Price Change Notice"
              value={MINIMUM_PRICE_NOTICE_LABEL}
            />
            {managementPlan ? (
              <>
                <AdminReadOnlyField
                  label="Pending Monthly Cost"
                  value={formatPendingCostDisplay(managementPlan)}
                />
                <AdminReadOnlyField
                  label="Effective From"
                  value={formatEffectiveFromOrDash(managementPlan)}
                />
              </>
            ) : null}

            {/* Creation info on Edit */}
            {!isAdd ? (
              <>
                <AdminReadOnlyField
                  label="Created On"
                  value={formatAuditDisplay(formData.createdOn)}
                />
                <AdminReadOnlyField label="Created By" value={formData.createdBy || '—'} />
                <AdminReadOnlyField
                  label="Last Updated On"
                  value={formatAuditDisplay(formData.lastUpdatedOn)}
                />
                <AdminReadOnlyField
                  label="Last Updated By"
                  value={formData.lastUpdatedBy || '—'}
                />
              </>
            ) : null}
          </>
        ) : (
          <AdminSupportingText className="text-mediumgray mb-0">
            Select a Source Plan to configure Pricing Plan settings.
          </AdminSupportingText>
        )}

        {showSaveButton ? (
          <div className="flex items-center justify-end gap-3 pt-2">
            <AdminHeaderAction type="submit">Save</AdminHeaderAction>
          </div>
        ) : null}
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default PlanDetail;
